import io, { Socket } from 'socket.io-client';
import { tokenProvider } from './apis';
import { config } from './config';

interface SocketConnection {
  socket: Socket | null;
  isInitialized: boolean;
  initializingPromise: Promise<void> | null;
  isConnected: boolean;
  activeGroup?: string; // Track active group for re-joining on reconnect
}

interface NegotiateResponse {
  endpoint: string;
  path: string;
  token: string;
  hub: string;
  expires_in: number;
}

class MultiHubSocketService {
  private baseUrl: string;
  private sockets: Map<string, SocketConnection> = new Map();
  private currentUserId: string | null = null;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || config.baseUrl || window.location.origin;
  }

  setCurrentUserId(userId: string | null) {
    this.currentUserId = userId;
  }

  private async negotiate(hub: string): Promise<NegotiateResponse | null> {
    try {
      if (!this.currentUserId) {
        console.warn(`[MultiHubSocket] [${hub}] negotiate skipped — no currentUserId`);
        return null;
      }

      const negotiateUrl = `${this.baseUrl}/api/webpubsub/negotiate`;

      let authToken = null;

      if (tokenProvider) {
        try {
          authToken = await tokenProvider();
        } catch (tokenErr) {
          console.warn(`[MultiHubSocket] [${hub}] tokenProvider threw:`, tokenErr);
          return null;
        }
      }

      if (!authToken) {
        console.warn(`[MultiHubSocket] [${hub}] negotiate skipped — no auth token (tokenProvider=${!!tokenProvider})`);
        return null;
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      };

      const requestBody = { hub, user_id: this.currentUserId };
      console.debug(`[MultiHubSocket] [${hub}] negotiate POST ${negotiateUrl}`, requestBody);
      const negotiateResponse = await fetch(negotiateUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!negotiateResponse.ok) {
        const errorText = await negotiateResponse.text().catch(() => '');
        console.error(`[MultiHubSocket] [${hub}] negotiate failed: ${negotiateResponse.status} ${negotiateResponse.statusText}`, errorText);
        return null;
      }

      const negotiateData = await negotiateResponse.json();
      console.debug(`[MultiHubSocket] [${hub}] negotiate OK — endpoint=${negotiateData.endpoint}`);
      return negotiateData as NegotiateResponse;
    } catch (error) {
      console.error(`[MultiHubSocket] [${hub}] negotiate exception:`, error);
      return null;
    }
  }

  async connectToHub(hub: string): Promise<boolean> {
    const existing = this.sockets.get(hub);
    if (existing?.isInitialized && existing?.socket?.connected) {
      return true;
    }

    if (existing?.initializingPromise) {
      try {
        await existing.initializingPromise;
        const result = existing.isConnected;
        return result;
      } catch (err) {
        return false;
      }
    }

    const initPromise = this._doConnect(hub);
    const connection = this.sockets.get(hub) || {
      socket: null,
      isInitialized: false,
      initializingPromise: null,
      isConnected: false
    };
    connection.initializingPromise = initPromise;
    this.sockets.set(hub, connection);

    try {
      await initPromise;
      const result = this.sockets.get(hub)?.isConnected ?? false;
      return result;
    } finally {
      connection.initializingPromise = null;
    }
  }

  private async _doConnect(hub: string): Promise<void> {
    try {
      const negotiateData = await this.negotiate(hub);
      if (!negotiateData) {
        console.warn(`[MultiHubSocket] [${hub}] _doConnect aborted — negotiate returned null`);
        return;
      }
      console.debug(`[MultiHubSocket] [${hub}] connecting to ${negotiateData.endpoint}${negotiateData.path}`);

      const socketOptions = {
        path: negotiateData.path,
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
        reconnectionAttempts: 20,
        upgrade: true,
        timeout: 30000,
        query: {
          access_token: negotiateData.token
        }
      };

      const existing = this.sockets.get(hub);
      if (existing?.socket?.connected) {
        existing.socket.disconnect();
      }

      const socket = io(negotiateData.endpoint, socketOptions as any);

      const connection = this.sockets.get(hub) || {
        socket: null,
        isInitialized: false,
        initializingPromise: null,
        isConnected: false
      };
      connection.socket = socket;
      connection.isInitialized = true;
      this.sockets.set(hub, connection);

      return new Promise<void>((resolve, reject) => {
        const connectTimeout = setTimeout(() => {
          reject(new Error(`[MultiHubSocket] [${hub}] Connection timeout - socket did not connect within 30s`));
        }, 30000);

        socket.on('connect', () => {
          clearTimeout(connectTimeout);
          console.debug(`[MultiHubSocket] [${hub}] ✅ connected (sid=${socket.id})`);
          const conn = this.sockets.get(hub);
          if (conn) {
            conn.isConnected = true;
            if (conn.activeGroup) {
              socket.emit('join', { group: conn.activeGroup });
            }
          }
          resolve();
        });

        socket.on('disconnect', (reason: string) => {
          const conn = this.sockets.get(hub);
          if (conn) {
            conn.isConnected = false;
          }
        });

        socket.on('reconnect', (attemptNumber: number) => {
          const conn = this.sockets.get(hub);
          if (conn) {
            conn.isConnected = true;
            if (conn.activeGroup) {
              socket.emit('join', { group: conn.activeGroup });
            }
          }
        });

        socket.on('reconnect_attempt', () => {
        });

        socket.on('reconnect_failed', () => {
        });

        socket.on('connect_error', (err) => {
          clearTimeout(connectTimeout);
          console.error(`[MultiHubSocket] [${hub}] ❌ connect_error:`, err?.message || err);
          const conn = this.sockets.get(hub);
          if (conn) {
            conn.isInitialized = false;
            conn.isConnected = false;
          }
          reject(err);
        });

        socket.on('error', (err) => {
        });
      });
    } catch (error) {
      console.error(`[MultiHubSocket] [${hub}] _doConnect exception:`, error);
    }
  }

  emit(hub: string, event: string, data: any = {}): boolean {
    const connection = this.sockets.get(hub);
    if (!connection?.socket) {
      return false;
    }
    try {
      if (event === 'join' && data?.group) {
        connection.activeGroup = data.group;
      }
      if (event === 'leave') {
        connection.activeGroup = undefined;
      }

      connection.socket.emit(event, data);
      return true;
    } catch (error) {
      return false;
    }
  }
  on(hub: string, event: string, callback: (...args: any[]) => void): void {
    const connection = this.sockets.get(hub);
    if (!connection?.socket) {
      return;
    }

    if (event === '*') {
      // Wrap callback so we can pass eventName as last arg.
      // Store wrapper on callback so off('*', callback) can retrieve it for offAny.
      const wrapper = (eventName: string, ...args: any[]) => {
        callback(...args, eventName);
      };
      (callback as any).__onAnyWrapper = wrapper;
      connection.socket.onAny(wrapper);
      return;
    }

    connection.socket.on(event, (...args: any[]) => {
      callback(...args);
    });
  }

  once(hub: string, event: string, callback: (...args: any[]) => void): void {
    const connection = this.sockets.get(hub);
    if (!connection?.socket) {
      return;
    }
    connection.socket.once(event, callback);
  }

  /**
   * Remove a specific listener. For '*' (onAny) listeners, uses socket.io's offAny.
   * This allows removing one handler without nuking all listeners on the hub.
   */
  off(hub: string, event: string, callback: (...args: any[]) => void): void {
    const connection = this.sockets.get(hub);
    if (!connection?.socket) return;

    if (event === '*') {
      // Use the stored wrapper from .on() so offAny gets the exact reference
      const wrapper = (callback as any).__onAnyWrapper || callback;
      (connection.socket as any).offAny(wrapper);
    } else {
      connection.socket.off(event, callback);
    }
  }

  removeListener(hub: string, event: string): void {
    const connection = this.sockets.get(hub);
    if (!connection?.socket) return;
    connection.socket.removeListener(event);
  }

  removeAllListeners(hub: string, event?: string): void {
    const connection = this.sockets.get(hub);
    if (!connection?.socket) return;
    if (event) {
      connection.socket.removeAllListeners(event);
    } else {
      connection.socket.removeAllListeners();
    }
  }

  isConnected(hub: string): boolean {
    return this.sockets.get(hub)?.isConnected ?? false;
  }

  disconnectHub(hub: string): void {
    const connection = this.sockets.get(hub);
    if (connection?.socket) {
      connection.socket.disconnect();
    }
  }

  disconnectAll(): void {
    for (const [hub, connection] of this.sockets.entries()) {
      if (connection.socket) {
        connection.socket.disconnect();
      }
    }
    this.sockets.clear();
  }

  getConnectedHubs(): string[] {
    const connected: string[] = [];
    for (const [hub, connection] of this.sockets.entries()) {
      if (connection.isConnected) connected.push(hub);
    }
    return connected;
  }

  getStatus(): { hub: string; connected: boolean; initialized: boolean }[] {
    const status: { hub: string; connected: boolean; initialized: boolean }[] = [];
    for (const [hub, connection] of this.sockets.entries()) {
      status.push({
        hub,
        connected: connection.isConnected,
        initialized: connection.isInitialized
      });
    }
    return status;
  }
}

let multiHubService: MultiHubSocketService | null = null;

export const getMultiHubSocketService = (baseUrl?: string): MultiHubSocketService => {
  if (!multiHubService) {
    multiHubService = new MultiHubSocketService(baseUrl);
  }
  return multiHubService;
};

export default MultiHubSocketService;
